/**
 * src/components/primitives/Skeleton.tsx — loading placeholder primitive (FR-008).
 *
 * Render a neutral shimmer block sized to the affordance it stands in for.
 * Used as the explicit RESOLVING-state output of `AccessGate` so the
 * underlying control never leaks into the DOM during the few-hundred-ms
 * window before the Convex member-row subscription resolves.
 *
 * Ported verbatim from the v.3 prototype primitive
 * (`primitives/index.tsx:512-514`). The `circa-skeleton` class is the
 * load-bearing identifier — `tests/primitives/access-gate.test.ts`
 * accepts `.circa-skeleton`, `[aria-busy="true"]`, or `[role="progressbar"]`
 * as proof that the gate is rendering a loading placeholder (not the
 * privileged control).
 *
 * The class styles (animation, background) live in the global stylesheet
 * `src/styles/globals.css` (alongside the other `circa-*` design-token
 * classes) — this file does not redeclare them inline.
 */
import React, { type CSSProperties } from 'react';

export interface SkeletonProps {
  /** Width — number → `px`, string → passthrough. Default `'100%'`. */
  w?: number | string;
  /** Height — number → `px`, string → passthrough. Default `16`. */
  h?: number | string;
  /** Border radius. Default `8`. */
  r?: number | string;
  /** Optional style override (merged after the size/radius). */
  style?: CSSProperties;
  /**
   * Marks the skeleton as a live "busy" placeholder for assistive tech.
   * The AccessGate primitive passes `aria-busy="true"` so the test suite
   * can detect the loading state without relying on the CSS class alone.
   */
  'aria-busy'?: boolean | 'true' | 'false';
  /** Accessible role override (e.g. `progressbar`). */
  role?: string;
}

export function Skeleton({
  w = '100%',
  h = 16,
  r = 8,
  style,
  'aria-busy': ariaBusy,
  role,
}: SkeletonProps): React.ReactElement {
  return (
    <div
      className="circa-skeleton"
      role={role}
      aria-busy={ariaBusy}
      style={{ width: w, height: h, borderRadius: r, ...style }}
    />
  );
}

export default Skeleton;
